import { Injectable, InjectionToken, Injector, Type } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmModalComponent } from './confirm-modal/confirm-modal.component';
import {
  ErrorModalComponent,
  ErrorModalData
} from './error-modal/error-modal.component';
import { TranslateOptions } from './utils/translate-options';

export const MODAL_DATA = new InjectionToken('MODAL_DATA');

// We use CANCEL for any modal rejection (e.g. dismiss, backdrop click, ESC key)
// to have a unified way to handle cancellations in the calling code since
// we don't care about the specific reason for cancellation in most cases.
export const CANCEL = 'CANCEL' as const;

export type ModalResult<T> = T | typeof CANCEL;

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  constructor(private readonly ngbModal: NgbModal) {}

  public async open<D, R>(
    component: Type<unknown>,
    data?: D,
    scrollable?: boolean,
    size?: 'sm' | 'lg' | 'xl' | string
  ): Promise<ModalResult<R>> {
    const modalRef = await this.ngbModal
      .open(component, {
        centered: true,
        keyboard: true,
        injector: this.createDataInjector(data),
        scrollable: scrollable,
        size: size
      })
      .result.catch(() => CANCEL);

    return modalRef;
  }

  public confirm<D>(component: Type<unknown>, data: D): Promise<boolean> {
    const modalRef = this.ngbModal.open(component, {
      centered: true,
      keyboard: true,
      injector: this.createDataInjector(data)
    });

    return modalRef.result.then(
      () => true,
      () => false
    );
  }

  public confirmDelete(options?: TranslateOptions): Promise<boolean> {
    return this.confirm(ConfirmModalComponent, {
      titleTranslationKey:
        options?.title?.key ?? 'common.modal.confirm_delete.title',
      messageTranslationKey:
        options?.message?.key ?? 'common.modal.confirm_delete.message',
      titleParameters: options?.title?.params,
      messageParameters: options?.message?.params
    });
  }

  public confirmUnsavedChanges(): Promise<boolean> {
    const prefix = 'common.modal.unsaved_changes';

    return this.confirm(ConfirmModalComponent, {
      titleTranslationKey: `${prefix}.title`,
      messageTranslationKey: `${prefix}.message`
    });
  }

  public error(data: ErrorModalData): Promise<boolean> {
    const modalRef = this.ngbModal.open(ErrorModalComponent, {
      centered: true,
      keyboard: true,
      backdrop: 'static',
      injector: this.createDataInjector(data)
    });

    return modalRef.result.then(
      () => true,
      () => false
    );
  }

  private createDataInjector<T>(data: T): Injector {
    return Injector.create({
      providers: [
        {
          provide: MODAL_DATA,
          useValue: data
        }
      ]
    });
  }
}
